import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { StaleBuildNavigator } from "@/components/StaleBuildNavigator";

// Keep only the auth entry path eager. Everything behind routing is lazy so
// Safari on iPad does not parse the full dashboard shell before first paint.
import Auth from "./pages/Auth";
// Direct import to avoid a broken lazy chunk on iPad Safari where the
// dynamic import for /lead/:id was failing with "Importing a module script failed".
import LeadDetail from "./pages/LeadDetail";
// The shell and the dashboard are needed on every visit, so keep them in the
// main bundle — splitting them only adds two more files that can fail to load.
import { AppLayout } from "@/components/AppLayout";
import Dashboard from "./pages/Dashboard";

// Lazy-loaded route chunks — each becomes its own bundle so a transient
// fetch failure for one route cannot blank the entire app, and iOS Safari
// only parses what the current route actually needs.
const Index = lazyWithRetry(() => import("./pages/Index"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Users = lazyWithRetry(() => import("./pages/Users"));
const Suppliers = lazyWithRetry(() => import("./pages/Suppliers"));
const Products = lazyWithRetry(() => import("./pages/Products"));
const Destinations = lazyWithRetry(() => import("./pages/Destinations"));
const Clients = lazyWithRetry(() => import("./pages/Clients"));
const QuoteView = lazyWithRetry(() => import("./pages/QuoteView"));
const QuoteComparisonView = lazyWithRetry(() => import("./pages/QuoteComparisonView"));
const QuotePaymentSuccess = lazyWithRetry(() => import("./pages/QuotePaymentSuccess"));
const SupplierQuoteRequestView = lazyWithRetry(() => import("./pages/SupplierQuoteRequestView"));
const Issues = lazyWithRetry(() => import("./pages/Issues"));
const BrandSettings = lazyWithRetry(() => import("./pages/BrandSettings"));
const ProjectSetupWizard = lazyWithRetry(() => import("./pages/ProjectSetupWizard"));
const ProjectSetupSummary = lazyWithRetry(() => import("./pages/ProjectSetupSummary"));
const ExecutiveDashboard = lazyWithRetry(() => import("./pages/ExecutiveDashboard"));
const Sales = lazyWithRetry(() => import("./pages/departments/Sales"));
const Marketing = lazyWithRetry(() => import("./pages/departments/Marketing"));
const Finance = lazyWithRetry(() => import("./pages/departments/Finance"));
const Research = lazyWithRetry(() => import("./pages/departments/Research"));
const SnapStackOperations = lazyWithRetry(() => import("./pages/operations/SnapStackOperations"));
const CoffeebeansOperations = lazyWithRetry(() => import("./pages/operations/CoffeebeansOperations"));
const NewCoffeebeansBooking = lazyWithRetry(() => import("./pages/operations/NewCoffeebeansBooking"));
const CreativeTravelOperations = lazyWithRetry(() => import("./pages/operations/CreativeTravelOperations"));
const NewCreativeTravelBooking = lazyWithRetry(() => import("./pages/operations/NewCreativeTravelBooking"));
const BookExperience = lazyWithRetry(() => import("./pages/BookExperience"));
const SupplierConfirm = lazyWithRetry(() => import("./pages/SupplierConfirm"));
const ConfirmBooking = lazyWithRetry(() => import("./pages/ConfirmBooking"));
const AvailabilityManagement = lazyWithRetry(() => import("./pages/AvailabilityManagement"));
const PromoCodeManagement = lazyWithRetry(() => import("./pages/PromoCodeManagement"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminChannels = lazyWithRetry(() => import("./pages/admin/Channels"));
const AdminMenuManager = lazyWithRetry(() => import("./pages/admin/MenuManager"));
const AdminSensitiveDocPurge = lazyWithRetry(() => import("./pages/admin/SensitiveDocPurge"));
const AdminTermsLibrary = lazyWithRetry(() => import("./pages/admin/TermsLibrary"));
const AdminEmailTemplates = lazyWithRetry(() => import("./pages/admin/EmailTemplates"));
const CompanyAdmin = lazyWithRetry(() => import("./pages/portal/CompanyAdmin"));
const CompanyAdminHR = lazyWithRetry(() => import("./pages/portal/companyAdmin/HR"));
const CompanyAdminLegal = lazyWithRetry(() => import("./pages/portal/companyAdmin/Legal"));
const CompanyAdminSupplierRates = lazyWithRetry(() => import("./pages/portal/companyAdmin/SupplierRates"));
const CompanyAdminOurRates = lazyWithRetry(() => import("./pages/portal/companyAdmin/OurRates"));
const CompanyAdminRegistration = lazyWithRetry(() => import("./pages/portal/companyAdmin/CompanyRegistration"));
const CompanyAdminInsurance = lazyWithRetry(() => import("./pages/portal/companyAdmin/Insurance"));
const CompanyAdminSARS = lazyWithRetry(() => import("./pages/portal/companyAdmin/SARS"));
const CompanyAdminFairTrade = lazyWithRetry(() => import("./pages/portal/companyAdmin/FairTrade"));
const PaymentWebhook = lazyWithRetry(() => import("./pages/PaymentWebhook"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const AcceptInvitation = lazyWithRetry(() => import("./pages/AcceptInvitation"));
const SessionBriefing = lazyWithRetry(() => import("./pages/SessionBriefing"));
const GuideReport = lazyWithRetry(() => import("./pages/GuideReport"));
const EmbedBooking = lazyWithRetry(() => import("./pages/EmbedBooking"));
const EmbedBookingSuccess = lazyWithRetry(() => import("./pages/EmbedBookingSuccess"));
const EmbedConciergeBooking = lazyWithRetry(() => import("./pages/EmbedConciergeBooking"));
const PayBooking = lazyWithRetry(() => import("./pages/PayBooking"));
const PayConciergeBooking = lazyWithRetry(() => import("./pages/PayConciergeBooking"));
const ProgrammeView = lazyWithRetry(() => import("./pages/ProgrammeView"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const ReportView = lazyWithRetry(() => import("./pages/ReportView"));
const DiscoverTours = lazyWithRetry(() => import("./pages/DiscoverTours"));
const Notepad = lazyWithRetry(() => import("./pages/Notepad"));
const Projects = lazyWithRetry(() => import("./pages/Projects"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const ClientBrandedProgrammeView = lazyWithRetry(() => import("./pages/ClientBrandedProgrammeView"));
const ClientBrandedQuoteView = lazyWithRetry(() => import("./pages/ClientBrandedQuoteView"));
const StudentIntakeForm = lazyWithRetry(() => import("./pages/StudentIntakeForm"));
const ClientIntakeForm = lazyWithRetry(() => import("./pages/ClientIntakeForm"));
const IntakeShareView = lazyWithRetry(() => import("./pages/IntakeShareView"));
const SharedDocuments = lazyWithRetry(() => import("./pages/SharedDocuments"));
const IntakeFixForm = lazyWithRetry(() => import("./pages/IntakeFixForm"));
const EmbedContact = lazyWithRetry(() => import("./pages/EmbedContact"));
const AgentPortalLogin = lazyWithRetry(() => import("./pages/portal/AgentPortalLogin"));
const EmbedAgentSignup = lazyWithRetry(() => import("./pages/EmbedAgentSignup"));
const EmbedWeekendSpecial = lazyWithRetry(() => import("./pages/EmbedWeekendSpecial"));
const WeekendSpecials = lazyWithRetry(() => import("./pages/WeekendSpecials"));
const WeekendSpecialsOperations = lazyWithRetry(() => import("./pages/operations/WeekendSpecialsOperations"));
const EmbedCTAContact = lazyWithRetry(() => import("./pages/EmbedCTAContact"));
const EmailUnsubscribe = lazyWithRetry(() => import("./pages/EmailUnsubscribe"));
const EmailPreferences = lazyWithRetry(() => import("./pages/EmailPreferences"));
const CTATermsPublic = lazyWithRetry(() => import("./pages/CTATermsPublic"));
const CTATermsAcceptance = lazyWithRetry(() => import("./pages/CTATermsAcceptance"));
// Public token-gated portals
const SupplierDashboard = lazyWithRetry(() => import("./pages/portal/SupplierDashboard"));
const SupplierPortalLogin = lazyWithRetry(() => import("./pages/portal/SupplierPortalLogin"));
const ClientDashboard = lazyWithRetry(() => import("./pages/portal/ClientDashboard"));
const AgentDashboard = lazyWithRetry(() => import("./pages/portal/AgentDashboard"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system">
      <ErrorBoundary>
        <TooltipProvider>
          <Toaster />
          <Sonner />

          <BrowserRouter>
            <StaleBuildNavigator />
            <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
              {/* Public routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/quote/:id" element={<QuoteView />} />
              <Route path="/compare/:token" element={<QuoteComparisonView />} />
              <Route path="/quote-payment-success" element={<QuotePaymentSuccess />} />
              <Route path="/supplier-quote-request/:shareToken" element={<SupplierQuoteRequestView />} />
              <Route path="/book-experience" element={<BookExperience />} />
              <Route path="/embed/book" element={<EmbedBooking />} />
              <Route path="/embed/booking-success" element={<EmbedBookingSuccess />} />
              <Route path="/embed/concierge" element={<EmbedConciergeBooking />} />
              <Route path="/embed/contact" element={<EmbedContact />} />
              <Route path="/embed/agent-signup" element={<EmbedAgentSignup />} />
              <Route path="/agents" element={<EmbedAgentSignup />} />
              <Route path="/embed/weekend-special" element={<EmbedWeekendSpecial />} />

              <Route path="/embed/cta-contact" element={<EmbedCTAContact />} />
              <Route path="/pay-booking/:token" element={<PayBooking />} />
              <Route path="/pay/:token" element={<PayConciergeBooking />} />
              <Route path="/supplier-confirm/:token" element={<SupplierConfirm />} />
              <Route path="/confirm-booking" element={<ConfirmBooking />} />
              <Route path="/programme/:id" element={<ProgrammeView />} />
              <Route path="/client-programme/:id" element={<ClientBrandedProgrammeView />} />
              <Route path="/client-quote/:id" element={<ClientBrandedQuoteView />} />
              <Route path="/report/:token" element={<ReportView />} />
              <Route path="/discover" element={<DiscoverTours />} />
              <Route path="/student-intake/:token" element={<StudentIntakeForm />} />
              <Route path="/client-intake/:token" element={<ClientIntakeForm />} />
              <Route path="/intake-share/:token" element={<IntakeShareView />} />
              <Route path="/intake-fix/:token" element={<IntakeFixForm />} />
              <Route path="/shared-documents/:token" element={<SharedDocuments />} />
              <Route path="/briefing/:token" element={<SessionBriefing />} />
              <Route path="/guide/report/:token" element={<GuideReport />} />

              {/* Creative Travel Africa terms (PUBLIC) */}
              <Route path="/cta/terms" element={<CTATermsPublic />} />
              <Route path="/cta/terms/accept/:token" element={<CTATermsAcceptance />} />

              {/* Email preferences (PUBLIC, tokenised) */}
              <Route path="/unsubscribe/:token" element={<EmailUnsubscribe />} />
              <Route path="/preferences/:token" element={<EmailPreferences />} />

              {/* Supplier Portal (PUBLIC) */}
              <Route path="/portal/supplier" element={<SupplierPortalLogin />} />
              <Route path="/portal/supplier/:token" element={<SupplierDashboard />} />

              {/* Client Portal (PUBLIC) */}
              <Route path="/portal/client/:token" element={<ClientDashboard />} />

              {/* Agent Portal (PUBLIC) */}
              <Route path="/portal/agent" element={<AgentPortalLogin />} />
              <Route path="/portal/agent/:token" element={<AgentDashboard />} />

              <Route path="/accept-invitation" element={<AcceptInvitation />} />

              {/* Protected routes with AppLayout */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
              <Route path="/" element={<Dashboard />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/pipeline" element={<Index />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/lead/:id" element={<LeadDetail />} />
                <Route path="/users" element={<Users />} />
                <Route path="/suppliers" element={<Suppliers />} />
                <Route path="/products" element={<Products />} />
                <Route path="/availability-management" element={<AvailabilityManagement />} />
                <Route path="/promo-codes" element={<PromoCodeManagement />} />
                <Route path="/weekend-specials" element={<Navigate to="/operations/weekend-specials" replace />} />
                <Route path="/weekend-specials/pricing" element={<WeekendSpecials />} />
                <Route path="/operations/weekend-specials" element={<WeekendSpecialsOperations />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/issues" element={<Issues />} />
                <Route path="/brand-settings" element={<BrandSettings />} />
                <Route path="/project-setup" element={<ProjectSetupWizard />} />
                <Route path="/project-setup-summary" element={<ProjectSetupSummary />} />
                <Route path="/executive" element={<ExecutiveDashboard />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/notepad" element={<Notepad />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/project/:id" element={<ProjectDetail />} />
                <Route path="/department/sales" element={<Sales />} />
                <Route path="/department/marketing" element={<Marketing />} />
                <Route path="/department/operations" element={<Navigate to="/operations/snapstack" replace />} />
                <Route path="/department/finance" element={<Finance />} />
                <Route path="/department/research" element={<Research />} />
                <Route path="/operations/snapstack" element={<SnapStackOperations />} />
                <Route path="/operations/coffeebeans" element={<CoffeebeansOperations />} />
                <Route path="/operations/coffeebeans/new-booking" element={<NewCoffeebeansBooking />} />
                <Route path="/operations/creative-travel" element={<CreativeTravelOperations />} />
                <Route path="/operations/creative-travel/new-booking" element={<NewCreativeTravelBooking />} />
                <Route path="/operations/availability" element={<AvailabilityManagement />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/channels" element={<AdminChannels />} />
                <Route path="/admin/menu-manager" element={<AdminMenuManager />} />
                <Route path="/admin/sensitive-documents" element={<AdminSensitiveDocPurge />} />
                <Route path="/admin/terms-library" element={<AdminTermsLibrary />} />
                <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
                <Route path="/portal/company-admin" element={<CompanyAdmin />} />
                <Route path="/portal/company-admin/hr" element={<CompanyAdminHR />} />
                <Route path="/portal/company-admin/legal" element={<CompanyAdminLegal />} />
                <Route path="/portal/company-admin/supplier-rates" element={<CompanyAdminSupplierRates />} />
                <Route path="/portal/company-admin/our-rates" element={<CompanyAdminOurRates />} />
                <Route path="/portal/company-admin/company-registration" element={<CompanyAdminRegistration />} />
                <Route path="/portal/company-admin/insurance" element={<CompanyAdminInsurance />} />
                <Route path="/portal/company-admin/sars" element={<CompanyAdminSARS />} />
                <Route path="/portal/company-admin/fair-trade" element={<CompanyAdminFairTrade />} />
                <Route path="/payment-webhook" element={<PaymentWebhook />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
